import { formatDate, t } from "@hakili/i18n";
import type { ReactElement } from "react";

/**
 * Lane E, the report round, M2: **the bank opens the report of its submitted file** , the
 * cabinet's paper and Hakili's sheet (« Lecture Hakili »), each with its SHA-256 fingerprint, the
 * provenance (cabinet, version, visa) and the validity (the grid's `report_validity_days`).
 * `GET /api/v1/bank/files/{id}/rapport` answers; a document that is missing is said in words and
 * draws no dead link; a file with no report says so (R81: never a blank).
 */
export interface ReportDocumentLink {
  readonly fileName: string;
  readonly contentHash: string;
  readonly url: string;
}

export interface BankReportView {
  readonly cabinetName: string;
  readonly version: number;
  readonly reportedAt: string | null;
  readonly validUntil: string | null;
  readonly visaByName: string | null;
  readonly paper: ReportDocumentLink | null;
  readonly sheet: ReportDocumentLink | null;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

function linkFrom(value: unknown): ReportDocumentLink | null {
  const one = rec(value);
  const url = str(one?.["url"]);
  if (one === null || url === null) return null;
  return { fileName: str(one["fileName"]) ?? "", contentHash: str(one["contentHash"]) ?? "", url };
}

export function reportFrom(answer: unknown): BankReportView | null {
  const report = rec(rec(answer)?.["report"]);
  if (report === null) return null;
  const cabinetName = str(report["cabinetName"]);
  const version = report["version"];
  if (cabinetName === null || typeof version !== "number") return null;
  return {
    cabinetName,
    version,
    reportedAt: str(report["reportedAt"]),
    validUntil: str(report["validUntil"]),
    visaByName: str(report["visaByName"]),
    paper: linkFrom(report["paper"]),
    sheet: linkFrom(report["sheet"]),
  };
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export function ReportPanel(props: { readonly view: BankReportView | null }): ReactElement {
  const { view } = props;
  const linkClass =
    "inline-flex min-h-[34px] items-center rounded-[10px] px-3 text-sm font-semibold text-[color:var(--hk-brand-strong)] hover:bg-[color:var(--hk-brand-wash)] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[color:var(--hk-focus)]";
  const missingClass = "m-0 inline-flex min-h-[34px] items-center px-3 text-sm text-gray-500";
  if (view === null) {
    return (
      <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4" data-testid="bank-rapport">
        <h3 className="text-sm font-semibold text-gray-900">{t("bank.rapport.titre")}</h3>
        <p className="mt-2 text-sm text-gray-500" data-testid="bank-rapport-aucun">
          {t("bank.rapport.aucun")}
        </p>
      </section>
    );
  }
  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4" data-testid="bank-rapport">
      <h3 className="text-sm font-semibold text-gray-900">{t("bank.rapport.titre")}</h3>
      <p className="mt-2 text-sm text-gray-700">
        {fill("bank.rapport.cabinet", {
          cabinet: view.cabinetName,
          version: String(view.version),
          date: view.reportedAt === null ? t("common.valeur.tiret") : formatDate(new Date(view.reportedAt)),
        })}
      </p>
      {view.visaByName === null ? null : (
        <p className="mt-1 text-sm text-gray-600">{fill("bank.rapport.visa", { nom: view.visaByName })}</p>
      )}
      <p className="mt-1 text-sm text-gray-600" data-testid="bank-rapport-validite">
        {view.validUntil === null
          ? t("common.valeur.tiret")
          : fill("bank.rapport.valable", { date: formatDate(new Date(`${view.validUntil}T00:00:00Z`)) })}
      </p>
      {/* `ARC-3` (D2 `ui.js:67` to `69`, `.expert .ex-b`): the two documents are text links on ONE row, the fingerprint line beneath,
          then « Intégrité vérifiée ». `BANK-AGENT` (agreed with lane A): the reading sheet's hash only behind that disclosure; the
          cabinet document's line stays in plain view. */}
      <div className="my-2.5 flex flex-wrap gap-2.5" data-testid="bank-rapport-liens">
        {view.paper === null ? (
          <p className={missingClass} data-testid="bank-rapport-papier-absent">
            {t("bank.rapport.papierAbsent")}
          </p>
        ) : (
          <a
            href={view.paper.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="bank-rapport-papier"
          >
            {t("bank.rapport.papierOuvrir")}
          </a>
        )}
        {view.sheet === null ? (
          <p className={missingClass} data-testid="bank-rapport-lecture-absente">
            {t("bank.rapport.lectureAbsente")}
          </p>
        ) : (
          <a
            href={view.sheet.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid="bank-rapport-lecture"
          >
            {t("bank.rapport.lectureOuvrir")}
          </a>
        )}
      </div>
      {view.paper === null ? null : (
        <p
          className="m-0 break-all font-mono text-sm text-[color:var(--hk-text-2)]"
          data-testid="bank-rapport-empreinte-papier"
        >
          {fill("bank.rapport.empreinte", { empreinte: view.paper.contentHash })}
        </p>
      )}
      {view.sheet === null ? null : (
        <details className="mt-1.5" data-testid="bank-rapport-integrite-lecture">
          <summary className="cursor-pointer text-sm text-[color:var(--hk-text-2)]">
            {t("bank.rapport.integrite")}
          </summary>
          <p
            className="mt-1 break-all font-mono text-sm text-[color:var(--hk-text-2)]"
            data-testid="bank-rapport-empreinte-lecture"
          >
            {fill("bank.rapport.empreinte", { empreinte: view.sheet.contentHash })}
          </p>
        </details>
      )}
    </section>
  );
}
